
import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import Icon from './Icon';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title, 
    message, 
    confirmLabel, 
    cancelLabel,
    variant = 'warning'
}) => {
    const { t } = useLanguage();

    if (!isOpen) return null;

    const confirmBtnClasses = variant === 'danger' 
        ? "bg-red-500 hover:bg-red-600 text-white" 
        : "bg-amber-400 hover:bg-amber-300 text-slate-900";

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <div className="w-full max-w-sm bg-slate-900 border border-amber-400/30 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-6 text-center">
                    <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${variant === 'danger' ? 'bg-red-500/20 text-red-500' : 'bg-amber-400/20 text-amber-400'}`}>
                        <Icon name={variant === 'danger' ? 'trash' : 'refresh'} className="w-8 h-8" />
                    </div>
                    <h2 className="text-xl font-display font-bold text-white mb-2">{title}</h2>
                    <p className="text-slate-400 text-sm leading-relaxed">{message}</p>
                </div>

                <div className="p-4 bg-black/20 flex gap-3">
                    <button 
                        onClick={onClose}
                        className="flex-1 py-2 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 transition-colors"
                    >
                        {cancelLabel || t('cancelButton')}
                    </button>
                    <button 
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                        className={`flex-1 py-2 font-bold rounded-lg transition-colors ${confirmBtnClasses}`}
                    >
                        {confirmLabel || t('confirmButton')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
